// frontend/src/lib/queryClient.ts — TanStack Query client.
//
// Per CONVENTIONS §Frontend state:
//
//   - staleTime: 0 — every component mount refetches (cached data shown
//     immediately while the refetch runs). Per-key override needed for
//     truly immutable reference data (e.g. country codes).
//   - refetchOnWindowFocus: false — tab-switching is not a meaningful
//     freshness signal for an ops app. Mount is the trigger.
//   - retry: 1 — pragmatic carry-over from dev branch (gives one chance to
//     ride out a flaky network hop without flooding the UI on real outages).
//   - retryDelay — the ONE retry waits TanStack's own backoff, except after
//     the server's BUSY answer (the fuse, ADR-079 §5): then it waits the
//     server's hint, ± 20 % so the tabs that met the same busy moment do not
//     return together (`retryDelayFor`, lib/refusal.ts — the bench's people
//     pace themselves by the same function). Mutations never retry (a busy
//     answer precedes any write, but a person's act is retried by the person).
//
// `['me']` overrides these in useMe (Layer 1+2 freshness — boot-essentials
// only). Everything else is mount-based + explicit invalidateQueries on
// mutation.
//
// `setClearQueryCache(...)` registers the cache-clear callback with the
// apiClient interceptor (401 handler) without creating a circular import.

import { QueryClient } from '@tanstack/react-query';
import { setClearQueryCache } from './apiClient.js';
import { retryDelayFor } from './refusal.js';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: (attempt, error) => retryDelayFor(attempt, error),
    },
    mutations: {
      retry: 0,
    },
  },
});

setClearQueryCache(() => queryClient.clear());
